import {useRef, useState} from 'react';

import classnames from 'classnames';

import {colors} from 'shells/colors/colors';
import {FormField} from 'shells/form/form-field';
import {RecurringRegularIcon} from 'shells/icon/svg/recurring-regular-icon';
import {RecurrenceSelect} from 'shells/recurrence/recurrence-select/recurrence-select';
import {getHumanReadableRecurrenceRule} from 'shells/row/task-row/helpers';
import {TaskCustomRecurrencePopover} from 'shells/row/task-row/task-custom-recurrence-popover/task-custom-recurrence-popover';

import {
    type TaskRecurrenceRuleFragment as RecurrenceRuleFragment,
    type RecurrenceRuleInput,
} from 'nutshell-graphql-types';

import styles from './recurrence-form-field.module.css';

type RecurrenceRule = Omit<RecurrenceRuleFragment, '__typename'>;

interface Props {
    recurrenceRule: RecurrenceRule | null;
    onRecurrenceRuleChange: (recurrenceRule: RecurrenceRuleInput | null) => void;
    referenceDatetime?: number | null;
    isDisabled?: boolean;
}

export function RecurrenceFormField(props: Props) {
    const [viewOptions, setViewOptions] = useState(false);
    const [viewCustomPopover, setViewCustomPopover] = useState(false);
    const buttonRef = useRef<HTMLButtonElement>(null);

    const hasRule = Boolean(props.recurrenceRule && props.recurrenceRule.rule);
    const triggerLabel = hasRule
        ? getHumanReadableRecurrenceRule(props.recurrenceRule)
        : 'Add schedule…';

    const handleCloseViewOptions = (e?: Event | null) => {
        if (e && typeof e.stopPropagation === 'function') {
            e.stopPropagation();
        }
        setViewOptions(false);
    };

    const handleCloseCustomPopover = (
        e?: React.SyntheticEvent<HTMLElement> | KeyboardEvent | Event
    ) => {
        if (e && typeof e.stopPropagation === 'function') {
            e.stopPropagation();
        }
        setViewCustomPopover(false);
    };

    const handleSaveCustomRecurrence = (input: RecurrenceRuleInput | null) => {
        props.onRecurrenceRuleChange(input ? {mode: input.mode, rule: input.rule} : null);
        setViewCustomPopover(false);
    };

    return (
        <FormField innerId='recurrenceRule' label='Repeats'>
            <button
                ref={buttonRef}
                type='button'
                disabled={props.isDisabled}
                className={classnames(styles.trigger, {
                    [styles['trigger--placeholder']]: !hasRule,
                })}
                onClick={() => setViewOptions(true)}
            >
                <RecurringRegularIcon
                    size={16}
                    style={{color: hasRule ? colors.blue : colors.greyLt}}
                    alignCenter={true}
                />
                <span className='truncate'>{triggerLabel}</span>
            </button>
            {viewOptions && (
                <RecurrenceSelect
                    anchor={buttonRef.current}
                    referenceDatetime={
                        props.referenceDatetime != null ? props.referenceDatetime : undefined
                    }
                    onRecurrenceRuleSelected={(rule) => {
                        props.onRecurrenceRuleChange(rule);
                        setViewOptions(false);
                    }}
                    onCustomSelected={() => {
                        setViewOptions(false);
                        setViewCustomPopover(true);
                    }}
                    onClose={handleCloseViewOptions}
                />
            )}
            {viewCustomPopover && (
                <TaskCustomRecurrencePopover
                    anchor={buttonRef.current}
                    onClose={handleCloseCustomPopover}
                    onRecurrenceRuleChange={handleSaveCustomRecurrence}
                    recurrenceRule={props.recurrenceRule}
                    referenceDatetime={props.referenceDatetime}
                />
            )}
        </FormField>
    );
}
